import { Locale } from 'date-fns'

export async function importDateLocale(
  locale: string
): Promise<Locale | undefined> {
  switch (locale) {
    case 'en':
      return (await import('date-fns/locale/en-GB')).enGB
    case 'es':
      return (await import('date-fns/locale/es')).es
    case 'pt':
      return (await import('date-fns/locale/pt')).pt
    case 'fr':
      return (await import('date-fns/locale/fr')).fr
    case 'it':
      return (await import('date-fns/locale/it')).it
    case 'de':
      return (await import('date-fns/locale/de')).de
    default:
      return Promise.resolve(undefined)
  }
}

const SUPPORTED_LOCALES = ['en', 'es', 'pt', 'fr', 'it', 'de'] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

const DEFAULT_LOCALE: SupportedLocale = 'en'

// Locales exposed to the user for now. Other translations exist in the repo but
// are not released yet, so the app runs only in Portuguese or English.
export const RELEASED_LOCALES = ['en', 'pt'] as const
export type ReleasedLocale = (typeof RELEASED_LOCALES)[number]

// Native names, intentionally not translated (a language is named in its own
// language regardless of the current UI locale).
export const LOCALE_LABELS: Record<ReleasedLocale, string> = {
  en: 'English',
  pt: 'Português',
}

const LOCALE_STORAGE_KEY = 'mydra-locale'

function isReleasedLocale(value: string): value is ReleasedLocale {
  return (RELEASED_LOCALES as readonly string[]).includes(value)
}

function getStoredLocale(): ReleasedLocale | null {
  if (typeof window === 'undefined') return null
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
  return stored && isReleasedLocale(stored) ? stored : null
}

export function persistLocale(locale: ReleasedLocale): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
}

function getBrowserPreferredLocale(): string | null {
  const browserLanguages =
    typeof navigator !== 'undefined'
      ? navigator.languages || [navigator.language]
      : []

  if (browserLanguages.length === 0) {
    return DEFAULT_LOCALE
  }

  for (const browserLang of browserLanguages) {
    if (!browserLang) continue

    const normalizedLang = browserLang.toLowerCase()

    if (SUPPORTED_LOCALES.includes(normalizedLang as SupportedLocale)) {
      return normalizedLang
    }

    const langCode = normalizedLang.split('-')[0]
    if (SUPPORTED_LOCALES.includes(langCode as SupportedLocale)) {
      return langCode
    }
  }

  return null
}

function getUrlLocale(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const url = new URL(window.location.href)

  const localeParam = url.searchParams.get('locale') ?? ''

  const localeMatch = localeParam.match(/^(en|es|pt|fr|it|de)$/)
  return localeMatch ? localeMatch[1] : null
}

export function getCurrentLocale(): string {
  // Only Portuguese and English are released for now, so any other source
  // value (URL, browser) falls back to English.
  const stored = getStoredLocale()
  if (stored) {
    return stored
  }

  const urlLocale = getUrlLocale()
  if (urlLocale && isReleasedLocale(urlLocale)) {
    return urlLocale
  }

  const browserLocale = getBrowserPreferredLocale()
  if (browserLocale?.startsWith('pt')) {
    return 'pt'
  }

  return DEFAULT_LOCALE
}

// Carry the chosen language across to another Mydra app (e.g. the dashboard),
// which lives on a different origin and can't read this app's localStorage.
// The target app reads `?locale=` on arrival and persists it.
export function appendLocaleParam(rawUrl: string): string {
  if (!rawUrl) return rawUrl
  try {
    const url = new URL(rawUrl, window.location.origin)
    url.searchParams.set('locale', getCurrentLocale())
    return url.toString()
  } catch {
    return rawUrl
  }
}

export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}

export function getSupportedLocales(): readonly SupportedLocale[] {
  return SUPPORTED_LOCALES
}

export async function loadMessages(locale: string) {
  try {
    const messages = await import(`../../locales/${locale}/strings.json`)
    return messages.default
  } catch (error) {
    console.error(`Failed to load messages for locale: ${locale}`, error)
    // Fallback to English
    const fallback = await import(
      `../../locales/${DEFAULT_LOCALE}/strings.json`
    )
    return fallback.default
  }
}
